/* expressionFieldValidator.js */

/**
 * @module expressionFieldValidator
 *
 * Post-Ajv validator for the JSON edit pane. Walks each rule's `ifExpression`
 * (MongoDB form) and flags:
 *   - leaf field names that aren't recognized device columns (catching typos
 *     like a misspelled `customFiieldValues.` prefix or a bogus device prop), and
 *   - operator-object keys that aren't valid mongo operators (e.g. `$edddq`).
 *
 * Notes:
 *   - `customFieldValues.<key>` paths are skipped for the field-name check —
 *     customFieldEnumValidator owns those — but their operators are still checked.
 *   - `thenExpression` / `elseExpression` leaf ids are owned by
 *     queryBuilderLeafValidator (queryBuilder form, not mongo).
 *
 * Field-name checks degrade safely: when `validFieldNames` is empty (column
 * registry not loaded) they're skipped rather than flagging every field.
 * Operator checks are a fixed set and always run. Signature matches
 * json_schema_validator: `(parsedValue, text) → Array<{line,column,message,jsonPointer}>`.
 */
const _ = require("lodash");
const {SchemaValidator} = require("@gluenetworks/json-editor");
const {walkMongoLeaves} = require("./expressionWalkers");

const CUSTOM_FIELD_PATH_PATTERN = /^customFieldValues\./;

// The mongo operators the form's queryBuilder getMongo emits for ifExpression
// leaves (see AdvancedFIlterRenderer). $and/$or are group-level, handled by the
// recursion, not leaf operator keys.
const MONGO_LEAF_OPERATORS = new Set([
    "$eq",
    "$ne",
    "$gt",
    "$gte",
    "$lt",
    "$lte",
    "$in",
    "$nin",
    "$regex",
    "$options",
    "$exists",
    "$not",
]);

const _formatError = (text, pointer, message) => {
    const {line, column} = SchemaValidator.pointerToLineColumn(text, pointer);
    return {line, column, message, jsonPointer: pointer};
};

const _checkOperators = (operatorObject, fieldName, pointer, ctx) => {
    if (!_.isObject(operatorObject) || _.isArray(operatorObject)) {
        return;
    }
    _.each(operatorObject, (value, operatorKey) => {
        if (!MONGO_LEAF_OPERATORS.has(operatorKey)) {
            ctx.errors.push(
                _formatError(
                    ctx.text,
                    `${pointer}/${fieldName}/${operatorKey}`,
                    `[operator] Unrecognized operator "${operatorKey}" on "${fieldName}"`
                )
            );
        }
    });
};

const _checkLeaf = (fieldName, operatorObject, leafPointer, ctx) => {
    // Operator check runs for every field (incl. customFieldValues.*).
    _checkOperators(operatorObject, fieldName, leafPointer, ctx);
    // Field-name check: device columns only, skipped for custom fields and
    // when the column registry wasn't loaded.
    if (CUSTOM_FIELD_PATH_PATTERN.test(fieldName)) {
        return;
    }
    if (ctx.validFieldNames.size && !ctx.validFieldNames.has(fieldName)) {
        ctx.errors.push(
            _formatError(
                ctx.text,
                `${leafPointer}/${fieldName}`,
                `[field] Unrecognized field "${fieldName}"`
            )
        );
    }
};

const _walkRule = (rule, basePointer, ctx) => {
    if (_.isObject(rule) && rule.ifExpression) {
        walkMongoLeaves(rule.ifExpression, `${basePointer}/ifExpression`, (field, ops, ptr) =>
            _checkLeaf(field, ops, ptr, ctx)
        );
    }
};

/**
 * @param {Array<string>} validFieldNames - recognized device column field names
 *        (e.g. Object.keys(columnConfig)). Empty → device-field checks skipped
 *        (operator checks still run).
 */
const buildExpressionFieldValidator = (validFieldNames = []) => {
    const validFieldNameSet = new Set(_.filter(validFieldNames, _.isString));

    return (parsedValue, text) => {
        if (!_.isObject(parsedValue)) {
            return [];
        }
        const ctx = {errors: [], text: text || "", validFieldNames: validFieldNameSet};
        if (_.isArray(parsedValue.rules)) {
            parsedValue.rules.forEach((rule, idx) => _walkRule(rule, `/rules/${idx}`, ctx));
        } else {
            _walkRule(parsedValue, "", ctx);
        }
        return ctx.errors;
    };
};

module.exports = {buildExpressionFieldValidator};
